import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import type { AppUser, UserRole, Profile } from '../lib/types'
import type { User, Session } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

interface AuthContextType {
  user: AppUser | null
  session: Session | null
  isLoading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ error?: string }>
  logout: () => Promise<void>
  isSuperAdmin: boolean
  isAdminPlus: boolean
  isAdmin: boolean
  isStaffPlus: boolean
  canEditArchive: boolean
  canViewInterviews: boolean
  canEditInterviews: boolean
  canManageUsers: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// Direct fetch for profile — bypasses Supabase JS client that hangs in Electron
async function fetchProfileDirect(userId: string, accessToken: string): Promise<Profile | null> {
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}&select=*`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/vnd.pgrst.object+json'
        }
      }
    )
    if (!res.ok) return null
    return await res.json()
  } catch {
    return null
  }
}

function buildAppUser(email: string, userId: string, profile: Profile | null): AppUser {
  const role: UserRole = profile?.role || 'staff'
  return {
    id: userId,
    email,
    displayName: profile?.display_name || email.split('@')[0] || 'Utente',
    role,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Use Supabase JS client for session management — with timeout protection
  useEffect(() => {
    let mounted = true

    const init = async () => {
      try {
        // Use Promise.race with timeout to prevent hanging 
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<typeof sessionPromise extends Promise<infer T> ? T : never>(
          (resolve) => setTimeout(() => resolve({ data: { session: null }, error: null } as any), 8000)
        )
        
        const { data: { session: s } } = await Promise.race([sessionPromise, timeoutPromise])

        if (s?.user && !sessionStorage.getItem('gi_session_active')) {
          const wasSessionOnly = localStorage.getItem('gi_session_only_flag')
          if (wasSessionOnly === 'true') {
            supabase.auth.signOut().catch(() => {})
            localStorage.removeItem('gi_session_only_flag')
            if (mounted) { setUser(null); setSession(null); setIsLoading(false) }
            return
          }
        }

        if (s?.user) sessionStorage.setItem('gi_session_active', 'true')

        if (mounted) {
          setSession(s)
          if (s?.user && s.access_token) {
            const profile = await fetchProfileDirect(s.user.id, s.access_token)
            if (profile) {
              setUser(buildAppUser(s.user.email || '', s.user.id, profile))
            } else {
              console.warn('Profile fetch failed, clearing stale session')
              supabase.auth.signOut().catch(() => {})
              setUser(null)
              setSession(null)
            }
          }
          setIsLoading(false)
        }
      } catch (err) {
        console.error('Auth init error:', err)
        supabase.auth.signOut().catch(() => {})
        if (mounted) {
          setUser(null)
          setSession(null)
          setIsLoading(false)
        }
      }
    }

    // Safety timeout
    const timeout = setTimeout(() => {
      if (mounted) setIsLoading(false)
    }, 10000)

    init().finally(() => clearTimeout(timeout))

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, s) => {
        if (!mounted) return
        setSession(s)
        if (s?.user && s.access_token) {
          sessionStorage.setItem('gi_session_active', 'true')
          const profile = await fetchProfileDirect(s.user.id, s.access_token)
          if (mounted) setUser(buildAppUser(s.user.email || '', s.user.id, profile))
        } else {
          setUser(null)
          sessionStorage.removeItem('gi_session_active')
        }
        setIsLoading(false)
      }
    )

    return () => { mounted = false; subscription.unsubscribe() }
  }, [])

  // Login using direct fetch to Supabase Auth API — bypasses JS client that hangs in Electron
  const login = async (email: string, password: string, rememberMe: boolean = true) => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000)

      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
        },
        body: JSON.stringify({ email, password }),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error_description: 'Login failed' }))
        return { error: errData.error_description || errData.msg || 'Invalid login credentials' }
      }

      const data = await res.json()

      // Now set the session in the Supabase client so the rest of the app works
      if (data.access_token && data.refresh_token) {
        await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token
        })
      }

      sessionStorage.setItem('gi_session_active', 'true')
      if (!rememberMe) {
        localStorage.setItem('gi_session_only_flag', 'true')
      } else {
        localStorage.removeItem('gi_session_only_flag')
      }
      return {}
    } catch (e: any) {
      console.error('Login fetch error:', e)
      return { error: e?.name === 'AbortError' ? 'Timeout — riprova' : (e?.message || 'Errore di connessione') }
    }
  }

  const logout = async () => {
    supabase.auth.signOut().catch(() => {})
    setUser(null)
    setSession(null)
  }

  const role = user?.role
  const isSuperAdmin = role === 'super_admin'
  const isAdminPlus = role === 'admin_plus' || isSuperAdmin
  const isAdmin = role === 'admin' || isAdminPlus
  const isStaffPlus = role === 'staff_plus' || isAdmin

  const canEditArchive = isStaffPlus
  const canViewInterviews = isAdmin
  const canEditInterviews = isAdminPlus
  const canManageUsers = isSuperAdmin

  return (
    <AuthContext.Provider value={{
      user, session, isLoading, login, logout,
      isSuperAdmin, isAdminPlus, isAdmin, isStaffPlus,
      canEditArchive, canViewInterviews, canEditInterviews, canManageUsers,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
